import React, { useContext, useEffect, useState, useRef } from 'react';
import UserContext from '../../../components/User_Context.js';
import { useParams } from 'react-router-dom';
import * as menu from "../../../assets/menus/menu.js";
import waiting from "../../../assets/images/waiting.svg";
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import * as breadcrumb from "../../../components/ui/breadcrumb.js";
import * as utils from "../../../components/utils.js";
import * as api from "../../../api/apiConnector.js";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useHandHygiene } from './hooks/use_hand_hygiene.js';
import HandHygieneForm from './components/hand_hygiene_form.js';
import { handHygieneLabels } from '../../_label/hand_hygiene/hand_hygiene_labels.js';
import { validateHandHygieneSheet, convertOpportunitiesFromObjectToDbTable } from './utils/hand_hygiene_validator.js';

// Create a single SweetAlert wrapper
const MySwal = withReactContent(Swal);

const Hand_Hygiene = () => {
    const { id } = useParams();
    const { t } = useTranslation('labels');
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);


    const {
        sheet,
        updateSheet,
        toSave,
        vocabs,
        config,
        sheetNumber,
        canEdit, // Get canEdit flag from hook
        filterAreasByDepartment,
        filterDepartmentsByAuthority,
        startTimeRef,
        endTimeRef
    } = useHandHygiene(id, currentUser);

    // Ref for collecting data from HandHygieneForm
    const handHygieneFormRef = useRef();

    const [preventQuit, setPreventQuit] = useState(false); // Track if there are unsaved changes
    const [isInitialized, setIsInitialized] = useState(false); // Track if the component has finished initializing
    const [initialSheetSnapshot, setInitialSheetSnapshot] = useState(null); // Store initial state for comparison

    // Function to create a deep comparison snapshot of the sheet
    const createSheetSnapshot = (sheetData) => {
        if (!sheetData) return null;
        return JSON.stringify(sheetData);
    };

    // Function to compare current sheet with initial snapshot
    const hasActualChanges = (currentSheet) => {
        if (!initialSheetSnapshot || !currentSheet) return false;
        const currentSnapshot = createSheetSnapshot(currentSheet);
        return currentSnapshot !== initialSheetSnapshot;
    };

    // Set initialized flag and capture initial snapshot when sheet is loaded
    useEffect(() => {
        if (sheet && !isInitialized) {
            // Small delay to ensure form is fully rendered before taking snapshot
            setTimeout(() => {
                setIsInitialized(true);
                setInitialSheetSnapshot(createSheetSnapshot(sheet));
                // Ensure preventQuit starts as false
                setPreventQuit(false);
            }, 100);
        }
    }, [sheet, isInitialized]);

    // Reset preventQuit when sheet changes externally (e.g., after save)
    useEffect(() => {
        if (sheet && isInitialized && !hasActualChanges(sheet)) {
            setPreventQuit(false);
        }
    }, [sheet, isInitialized, initialSheetSnapshot]);

    // Add event listeners to detect form changes
    useEffect(() => {
        // Don't add listeners until form is fully initialized
        if (!isInitialized) return;

        // We now rely on direct callbacks from components instead of generic event listeners
        // This is more reliable and avoids false positives during initialization

        return () => {
            // No cleanup needed since we're not adding generic listeners anymore
        };
    }, [isInitialized]); // Only depend on isInitialized

    // Use scroll preservation hook
    // useScrollPreservation();

    // Run on mount or when the user changes
    useEffect(() => {
        menu.write({ user: currentUser() });
    }, [currentUser]);

    // Update breadcrumbs when sheetNumber, id, or language changes
    useEffect(() => {
        if (!sheetNumber) return;
        breadcrumb.set([
            { link: "/", name: "Home" },
            {
                link: `/darm/handHygiene/${id}`,
                name: t('handHygieneSheet', { identifier: sheetNumber })
            }
        ]);
    }, [sheetNumber, id, t]);

    // Intercept breadcrumb and menu navigation clicks
    useEffect(() => {
        const handleLinkClick = async (event) => {
            // Only handle anchor links
            const target = event.target.closest('a');
            if (!target) return;

            // Check if it's a valid navigation link (excluding external links and specific toolbar buttons)
            const href = target.getAttribute('href');
            if (!href ||
                href.startsWith('http') ||
                href.startsWith('mailto') ||
                href === '#') return;

            // Exclude only the specific toolbar action buttons (save/delete/back arrow)
            const toolbarActions = target.closest('.d-flex.gap-3');
            if (toolbarActions) return;

            // Only intervene if there are unsaved changes and user can edit
            if (!preventQuit || !canEdit) return;

            event.preventDefault();
            event.stopPropagation();

            try {
                const confirmed = await MySwal.fire({
                    title: t('attention', { ns: 'labels' }),
                    text: t('unsavedChanges', { ns: 'labels' }),
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#aaa',
                    confirmButtonText: t('exitWithoutSaving', { ns: 'labels' }),
                    cancelButtonText: t('cancel', { ns: 'labels' })
                });

                if (confirmed.isConfirmed) {
                    setPreventQuit(false);
                    // Allow the navigation to proceed
                    window.location.href = href;
                }
            } catch (error) {
                // Handle error gracefully if modal fails
                console.warn('Navigation confirmation failed:', error);
            }
        };

        // Use capture phase to intercept before other handlers
        document.addEventListener('click', handleLinkClick, true);

        // Also add specific delegation for dynamically created menu items
        const handleMenuClick = async (event) => {
            const target = event.target.closest('a');
            if (!target) return;

            // Let the main handler deal with it
            await handleLinkClick(event);
        };

        // Add event delegation for menu items that are created dynamically
        if (window.$) {
            window.$(document).on('click.handHygiene', '.metismenu a, .breadcrumb a', handleMenuClick);
        }

        return () => {
            document.removeEventListener('click', handleLinkClick, true);
            // Clean up jQuery event delegation
            if (window.$) {
                window.$(document).off('click.handHygiene');
            }
        };
    }, [preventQuit, t]);

    // Delete the sheet
    const deleteOMS = async () => {
        const confirmed = await MySwal.fire({
            title: t('attention', { ns: 'labels' }),
            text: t('areYouSure', { ns: 'labels' }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#aaa',
            confirmButtonText: t('delete', { ns: 'labels' }),
            cancelButtonText: t('cancel', { ns: 'labels' })
        });

        if (confirmed.isConfirmed) {
            try {
                const result = await api.sendMessage("darm/handHygiene/deleteSheet", {
                    user: currentUser(),
                    body: { sheetId: id }
                });

                if (result?.success) {
                    Swal.fire({
                        title: t('success', { ns: 'labels' }),
                        icon: "success",
                        text: t('handHygieneDeletedSuccessfully', { ns: 'labels' }),
                        showConfirmButton: true,
                        timer: 2000
                    });

                    setTimeout(() => {
                        setPreventQuit(false);
                        navigate('/darm/handHygiene/finder');
                    }, 2100);
                } else {
                    throw new Error(result.message || 'Unknown error');
                }
            } catch (error) {
                Swal.fire({
                    title: t('error', { ns: 'labels' }),
                    icon: "error",
                    text: error.message,
                    showConfirmButton: true
                });
            }
        }
    };

    // Save the sheet
    const saveOMS = async () => {
        MySwal.fire({
            title: "INFO",
            icon: "info",
            text: t('savingData', { ns: 'labels' }),
            showConfirmButton: true,
            timer: 1500
        });

        // Collect current data from form
        const currentFormData = handHygieneFormRef.current?.getData();
        if (!currentFormData) {
            MySwal.fire({
                title: t('error', { ns: 'labels' }),
                icon: "error",
                text: "Unable to collect form data",
                showConfirmButton: true
            });
            return;
        }

        const handHygieneForBackend = utils.clone(currentFormData);

        if (handHygieneForBackend.ObservedOperators) {
            handHygieneForBackend.ObservedOperators = handHygieneForBackend.ObservedOperators.filter(operator => {
                if (!Array.isArray(operator.Opportunities) || operator.Opportunities.length === 0) {
                    return false;
                }

                const hasNonEmptyOpportunity = operator.Opportunities.some(opportunity => {
                    const hasSelectedAzioni = Array.isArray(opportunity.selectedAzioni) && opportunity.selectedAzioni.length > 0;
                    const hasSelectedOpportunita = Array.isArray(opportunity.selectedOpportunita) && opportunity.selectedOpportunita.length > 0;

                    return hasSelectedAzioni || hasSelectedOpportunita;
                });

                return hasNonEmptyOpportunity;
            });

            handHygieneForBackend.ObservedOperators.forEach(op => {
                op.SheetId = handHygieneForBackend.Id;

                op.Opportunities = op.Opportunities.filter(o => {
                    const hasSelectedAzioni = Array.isArray(o.selectedAzioni) && o.selectedAzioni.length > 0;
                    const hasSelectedOpportunita = Array.isArray(o.selectedOpportunita) && o.selectedOpportunita.length > 0;
                    return hasSelectedAzioni || hasSelectedOpportunita;
                });

                const realOpportunitiesData = [];

                if (op.Opportunities) {
                    op.Opportunities.forEach(o => {
                        const convertedOpportunities = convertOpportunitiesFromObjectToDbTable(o, op);
                        realOpportunitiesData.push(convertedOpportunities);
                    });
                }

                op.realOpportunitiesData = realOpportunitiesData;
            });
        }

        const errors = await validateHandHygieneSheet(handHygieneForBackend, config, t, vocabs);

        if (errors.length > 0) {
            MySwal.fire({
                title: t('attention', { ns: 'labels' }),
                icon: "error",
                html: errors.map(e => `<div style="text-align: left;">• ${e}</div>`).join(""),
                showConfirmButton: true
            });
        } else {           
            const save_result = await api.sendMessage("darm/handHygiene/saveHandHygieneSheet", {
                user: currentUser(),
                body: {
                    handHygiene: handHygieneForBackend
                }
            });

            if (save_result.success) {
                MySwal.fire({
                    title: t('success', { ns: 'labels' }),
                    icon: "success",
                    text: t('handHygieneSavedSuccesfully', { ns: 'labels' }),
                    showConfirmButton: true,
                    confirmButtonText: t('close', { ns: 'labels' }),
                    timer: 2000
                });

                // Reset preventQuit after successful save
                setPreventQuit(false);
                
                // Navigate to a new form after a short delay
                setTimeout(() => {
                    navigate('/darm/handHygiene', { replace: true });
                    // Reload if necessary to ensure a fresh state, 
                    // or just rely on the navigate if the route handles it
                    window.location.reload(); 
                }, 2000);
            } else {

                if (save_result.message === 'Unauthorized') {
                    MySwal.fire({
                        title: t('accessDenied', { ns: 'labels' }),
                        icon: "error",
                        text: t('youDoNotHavePermissionToModify', { ns: 'labels' }),
                        showConfirmButton: true,
                        confirmButtonText: t('close', { ns: 'labels' })
                    });
                }
                else {

                    MySwal.fire({
                        title: t('error', { ns: 'labels' }),
                        icon: "error",
                        text: t('handHygieneSavingError', { ns: 'labels' }),
                        showConfirmButton: true,
                        confirmButtonText: t('close', { ns: 'labels' })
                    });
                }

            }
        }
    };

    // Navigation guard function to check for unsaved changes
    const handleNavigationAttempt = async (targetPath, forceNavigation = false) => {
        // Don't show warning if user can't edit or there are no unsaved changes
        if (!preventQuit || !canEdit || forceNavigation) {
            navigate(targetPath);
            return true;
        }

        const confirmed = await MySwal.fire({
            title: t('attention', { ns: 'labels' }),
            text: t('unsavedChanges', { ns: 'labels' }),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#aaa',
            confirmButtonText: t('exitWithoutSaving', { ns: 'labels' }),
            cancelButtonText: t('cancel', { ns: 'labels' })
        });

        if (confirmed.isConfirmed) {
            setPreventQuit(false);
            navigate(targetPath);
            return true;
        }

        return false;
    };

    // Callback function to be called when any section has changes
    const handleFormDataChange = () => {
        if (!isInitialized) return;
        // Only track unsaved changes if user can edit
        if (canEdit) {
            setPreventQuit(true);
        }
    };

    // Main render of the Hand_Hygiene component: header + toolbar + scrollable body
    return (
        <div className="d-flex flex-column" style={{ height: '100vh' }}>

            {/* 1) HEADER + TOOLBAR (never scrolls) */}
            <header className="bg-white shadow-sm" style={{ position: 'sticky', top: 0, zIndex: 0 }}>
                <div className="container-fluid d-flex justify-content-between align-items-center py-2 fixed-toolbar">

                    {/* → breadcrumb navigation */}
                    <ol className="breadcrumb mb-0"></ol>

                    {/* → save / delete icons */}
                    <div className="d-flex gap-3">
                        <a
                            className="text-secondary fs-13"
                            onClick={() => handleNavigationAttempt('/darm/handHygiene/finder')}
                            role="button"
                            title="Torna al Finder"
                        >
                            <i className="fa-solid fa-arrow-left fa-2x"></i>
                        </a>
                        {/* Only show save button if user can edit */}
                        {canEdit && (
                            <a
                                className="text-primary fs-13"
                                onClick={() => {
                                    if (preventQuit) {
                                        saveOMS();
                                    } else {
                                        MySwal.fire({
                                            title: 'INFO',
                                            icon: 'info',
                                            text: t('nothingToSave', { ns: 'labels' }),
                                            showConfirmButton: true,
                                            timer: 1500,
                                        });
                                    }
                                }}
                                role="button"
                                title="Save data"
                                style={{ cursor: 'pointer' }}
                            >
                                <i className="fa-solid fa-floppy-disk fa-2x"></i>
                            </a>
                        )}
                        {/* Only show delete button if user can edit */}
                        {canEdit && (
                            <a
                                className="text-danger fs-13"
                                onClick={() => deleteOMS()}
                                role="button"
                                title="Delete data"
                            >
                                <i className="fa-solid fa-trash fa-2x"></i>
                            </a>
                        )}
                    </div>
                </div>
            </header>

            {/* 2) SCROLLABLE CONTENT AREA*/}
            <main className="flex-grow-1 overflow-auto">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-xl-12">
                            <div className="card">
                                <div className="card-body p-0">
                                    <div
                                        className="tab-content"
                                        id="tabs-content"
                                        style={{ padding: 15 }}
                                    >
                                        {sheet ? (
                                            <HandHygieneForm
                                                ref={handHygieneFormRef}
                                                sheet={sheet}
                                                vocabs={vocabs}
                                                config={config}
                                                sheetNumber={sheetNumber}
                                                filterAreasByDepartment={filterAreasByDepartment}
                                                filterDepartmentsByAuthority={filterDepartmentsByAuthority}
                                                onDataChange={handleFormDataChange}
                                            />
                                        ) : (
                                            <div className="text-center">
                                                <img src={waiting} alt="Waiting" />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}

export default Hand_Hygiene;